import { AgentProfile, AppPresence, UserRole } from "@closerplatform/spinner-openapi";
import { logger } from "@fidget/common/utils";
import { notEmpty } from "@fidget/common/utils/not-empty";
import { flatten, uniq } from "lodash";
import { flow, Instance, types } from "mobx-state-tree";
import { agentGroupsStoreGetter, servicesGetters, uiStoreGetter } from "../utils";
import { Agent, AgentModel, IAgent } from "./Agent";
import { AgentEventListener } from "./eventHandlers";
import { hardcodedCloserBotProfile } from "@fidget/common/models/hacks";

function createAgentPlaceholder(id: string): IAgent {
  return Agent.create({
    id,
    firstName: "Unknown",
    lastName: "Unknown",
    email: "unknown",
    isLoading: true,
    orgRole: "unknown"
  });
}

/**
 * Agent store model
 */
export const AgentStore = types
  .model("AgentStore", {
    agents: types.map(Agent),
    orgBot: types.maybe(Agent),
    agentsLastLoaded: types.maybe(types.number)
  })
  .views(servicesGetters)
  .views(uiStoreGetter)
  .views(agentGroupsStoreGetter)
  .views((self) => {
    const views = {
      get agentProfiles(): AgentProfile[] {
        return [...self.agents.values()] as AgentProfile[];
      },

      getById(id: string): IAgent | undefined {
        return self.agents.get(id);
      },

      getByIds(ids: string[]): AgentProfile[] {
        return ids.map((id) => self.agents.get(id)).filter(notEmpty);
      },

      getWithoutIds(ids: string[]): AgentProfile[] {
        return views.agentProfiles.filter((a) => !ids.includes(a.id));
      }
    };

    return views;
  })
  .actions((self) => {
    const actions = {
      fetchAgent: flow(function* fetchAgent(id: string, placeholder: IAgent) {
        const agent = self.agents.get(id);
        // check for placeholder or set it to avoid multiple requests
        if (agent && agent.isLoading) {
          return;
        }

        if (id === hardcodedCloserBotProfile.id) {
          self.agents.put(hardcodedCloserBotProfile);
          return;
        }

        self.agents.put(placeholder);
        try {
          const profile: AgentProfile = yield self.spinnerService.getAgentProfile(id);
          self.agents.put(profile);
        } catch (error) {
          logger.error({ message: "Couldn't get agent profile", error });
          throw new Error(`Could not fetch the agent with id ${id}`);
        }
      }),

      getAllAgentTagGroups: function getAllAgentTagGroups(agentId: string): string[] {
        const agent = self.agents.get(agentId);
        if (!agent) {
          return [];
        }

        const agentGroupsTagGroups = flatten(
          self.agentGroupsStore.getByIds(agent.groups).map((agentGroup) => agentGroup.tagGroupIds)
        );

        return uniq([...agentGroupsTagGroups, ...agent.tagGroups]);
      },

      setAgentProfiles(agents: AgentProfile[]) {
        agents.map((a) => AgentModel.fromApiDTO(a)).forEach((a) => self.agents.put(a));
      },

      loadAgentProfiles: flow(function* loadAgentProfiles() {
        try {
          const agents: AgentProfile[] = yield self.spinnerService.getAgentProfiles();
          actions.setAgentProfiles(agents.filter((a) => a.isActive));

          return agents;
        } catch (e) {
          logger.error({ message: "Couldn't load agent profiles", error: e });
          self.uiStore.addGenericErrorToast();
          throw e;
        }
      }),
      /**
       * Create Agent instance representing the organization, disguised as a bot.
       */
      createOrgBot: flow(function* createOrgBot(orgId: string) {
        if (!self.orgBot || self.orgBot.id !== orgId) {
          self.orgBot = Agent.create({
            id: orgId,
            email: "",
            role: UserRole.ORGANIZATION,
            firstName: "Bot",
            lastName: "",
            orgRole: ""
          });
          const botProfile: AgentProfile = yield self.spinnerService.getOrgBotProfile();
          self.orgBot = Agent.create({
            ...botProfile,
            id: orgId,
            role: UserRole.ORGANIZATION,
            presence: AppPresence.Available
          });
        }
      })
    };

    return actions;
  })
  .views((self) => ({
    /**
     * Load agent and add it to the collection of loaded agents
     * XXX: a view, so any references - including via AgentReferenceType -
     *   will automatically update after the agent is loaded
     */
    getOrLoad(id: string) {
      const agent = self.agents.get(id);
      if (agent) {
        return agent;
      }

      // try orgBot
      if (self.orgBot && id === self.orgBot.id) {
        return self.orgBot;
      }
      // otherwise create profile placeholder and queue profile fetching from backend
      const placeholder = createAgentPlaceholder(id);
      // FIXME: a hack proposed by MST devs
      //   https://github.com/mobxjs/mobx-state-tree/blob/master/packages/mobx-state-tree/__tests__/core/reference-custom.test.ts#L159
      //   setImmediate because `self.fetchAgent(id)` would cause side effects warning
      setImmediate(() => self.fetchAgent(id, placeholder));
      return placeholder;
    }
  }))
  // set up event listeners
  .actions((self) => {
    const eventListenerName = "AgentEventListener";
    const actions = {
      /**
       * Set agent as inactive (TODO: check)
       * @param {string} agentId [description]
       */
      removeAgent(agentId: string) {
        const agent = self.agents.get(agentId);
        if (agent) {
          agent.isActive = false;
        }
      },

      /**
       * Set agent as active (TODO: check)
       * @param {string} agentId [description]
       */
      restoreAgent(agentId: string) {
        const agent = self.agents.get(agentId);
        if (agent) {
          agent.isActive = true;
        }
      },

      clear() {
        self.agents.clear();
      },

      /**
       * MST lifecycle hook.
       * Subscribe to events.
       */
      afterCreate() {
        self.eventListener.addGlobalListener(
          new AgentEventListener(() => self as IAgentStore, eventListenerName)
        );
      },

      /**
       * MST lifecycle hook.
       * Unsubscribe from events.
       */
      beforeDestroy() {
        self.eventListener.removeGlobalListener(eventListenerName);
      }
    };
    return actions;
  });

export interface IAgentStore extends Instance<typeof AgentStore> {}
